import { useCallback, useRef, useState } from "react";

/**
 * Pairs with a `lazy(() => loader())` modal so the trigger button can show a
 * spinner while the dynamic-import chunk is being downloaded.
 *
 *   const loadFoo = () => import("@/components/PFoo/PFoo");
 *   const PFoo = lazy(() => loadFoo().then((m) => ({ default: m.PFoo })));
 *
 *   const { opening, open } = useLazyOpen(loadFoo);
 *   <PrimaryButton
 *     loading={opening}
 *     disabled={opening}
 *     onClick={() => open(() => setShowFoo(true))}
 *   />
 *
 * On subsequent opens the chunk is already cached so `opening` flips back to
 * false within a microtask — the spinner is invisible after first load.
 *
 * The in-flight guard lives in a ref so `open`'s identity stays stable across
 * the opening→done transition; consumers can pass it into deeper memoized
 * components without re-creating their handlers each cycle.
 */
export function useLazyOpen(loader: () => Promise<unknown>) {
  const [opening, setOpening] = useState(false);
  const inFlightRef = useRef(false);

  const open = useCallback(
    async (after: () => void) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setOpening(true);
      try {
        await loader();
        after();
      } finally {
        inFlightRef.current = false;
        setOpening(false);
      }
    },
    [loader],
  );

  return { opening, open };
}
